import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  InputAdornment, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem,
  Switch, TextField, Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CloseIcon from '@material-ui/icons/Close'
import DeleteIcon from '@material-ui/icons/Delete'
import SearchIcon from '@material-ui/icons/Search'
import AddIcon from '@material-ui/icons/Add'

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
// Sell figure to display/roll-up: the pricing engine's totalSell when set,
// otherwise a per-line total entered manually or imported (importedTotal).
const effectiveTotal = (li) => (li && li.totalSell > 0 ? li.totalSell : (li && li.importedTotal) || 0)
const isImported = (li) => li && !(li.totalSell > 0) && li.importedTotal > 0
const isMaterialMarkupLine = (li) =>
  String(li?.description || '').replace(/[^a-z]/gi, '').toLowerCase() === 'markup'
const invoiceNumberLabel = (value, fallback = 'Invoice') => {
  const invoiceNumber = String(value || '').trim()
  if (!invoiceNumber) return fallback
  if (/^invoice\b/i.test(invoiceNumber)) return invoiceNumber
  return invoiceNumber.startsWith('#') ? `Invoice ${invoiceNumber}` : `Invoice #${invoiceNumber}`
}

/* ── new-invoice project picker (creates a draft) ──────────────── */
const NewInvoiceDialog = ({ url, open, onClose, onCreated }) => {
  const [projects, setProjects] = useState(null)
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  useEffect(() => {
    if (!open) return
    axios.get(`${url}/api/bid-estimation/projects`)
      .then(res => setProjects(res.data || []))
      .catch(() => setProjects([]))
  }, [url, open])
  const filtered = useMemo(() => {
    if (!projects) return []
    const needle = q.trim().toLowerCase()
    return (needle ? projects.filter(p => (p.name || '').toLowerCase().includes(needle)) : projects).slice(0, 20)
  }, [projects, q])
  const choose = (p) => {
    setCreating(true)
    axios.post(`${url}/api/bid-estimation/invoice-grouping/drafts`, { projectId: p._id })
      .then(res => onCreated(res.data))
      .catch(e => window.alert(e.response?.data?.error || e.message))
      .then(() => setCreating(false))
  }
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Invoice — choose a bid project</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth variant="outlined" placeholder="Search bid projects…" value={q}
          onChange={e => setQ(e.target.value)} autoFocus disabled={creating}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        {projects === null || creating ? <Box mt={3} textAlign="center"><CircularProgress /></Box> : (
          <List>
            {filtered.map(p => (
              <ListItem button key={p._id} onClick={() => choose(p)}>
                <ListItemText primary={p.name} secondary={`${p.status || ''} · updated ${new Date(p.updatedAt).toLocaleDateString()}`} />
              </ListItem>
            ))}
            {!filtered.length && <Typography variant="body2" color="textSecondary" align="center">No matching projects.</Typography>}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button></DialogActions>
    </Dialog>
  )
}

/* ── drafts landing: every invoice draft across all projects ───── */
const DraftList = ({ url, onOpen }) => {
  const [drafts, setDrafts] = useState(null)
  const [newOpen, setNewOpen] = useState(false)
  const base = `${url}/api/bid-estimation/invoice-grouping`
  const load = useCallback(() => {
    axios.get(`${base}/drafts`).then(res => setDrafts(res.data || [])).catch(() => setDrafts([]))
  }, [base])
  useEffect(() => { load() }, [load])

  const deleteDraft = (d, e) => {
    e.stopPropagation()
    if (!window.confirm(`Delete invoice draft "${d.name}"? Its groups are removed (line items and prices are unaffected).`)) return
    axios.delete(`${base}/drafts/${d._id}`).then(load).catch(err => window.alert(err.response?.data?.error || err.message))
  }

  return (
    <Box maxWidth={760} mx="auto" mt={5}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5">Invoice Drafts</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}>
          New Invoice
        </Button>
      </Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Each draft is an independent working copy — group a project’s line items into invoices. Multiple drafts can run on the same project.
      </Typography>
      {drafts === null ? <Box mt={3} textAlign="center"><CircularProgress /></Box> : (
        <List>
          {drafts.map(d => (
            <ListItem button divider key={d._id} onClick={() => onOpen(d._id)}>
              <ListItemText
                primary={d.name}
                secondary={
                  `${d.projectName} · ${d.groupCount || 0} group${d.groupCount === 1 ? '' : 's'}` +
                  `${d.readyCount ? ` · ${d.readyCount} ready` : ''}` +
                  `${d.createdByName ? ` · ${d.createdByName}` : ''} · updated ${new Date(d.updatedAt).toLocaleDateString()}`
                }
              />
              {d.readyCount > 0 && <Chip size="small" label={`${d.readyCount} ready`} style={{ backgroundColor: '#4caf50', color: '#fff', marginRight: 8 }} />}
              <IconButton size="small" onClick={e => deleteDraft(d, e)}><DeleteIcon fontSize="small" /></IconButton>
            </ListItem>
          ))}
          {!drafts.length && (
            <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 24 }}>
              No invoice drafts yet. Click “New Invoice” to start one from a bid project.
            </Typography>
          )}
        </List>
      )}
      <NewInvoiceDialog url={url} open={newOpen} onClose={() => setNewOpen(false)} onCreated={d => { setNewOpen(false); onOpen(d._id) }} />
    </Box>
  )
}

/* ── Aspire opportunity picker (one invoice → one/many jobs) ───── */
const oppLabel = (o) =>
  `#${o.OpportunityNumber || o.OpportunityID} — ${o.OpportunityName || ''}${o.PropertyName ? ` (${o.PropertyName.trim()})` : ''}`

const OpportunityPicker = ({ url, value, onChange }) => {
  const [input, setInput] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const q = input.trim()
    if (q.length < 2) { setOptions([]); return }
    let active = true
    setLoading(true)
    const t = setTimeout(() => {
      axios.get(`${url}/api/bid-estimation/invoice-grouping/opportunities/search`, { params: { q } })
        .then(res => { if (active) setOptions(res.data || []) })
        .catch(() => { if (active) setOptions([]) })
        .then(() => { if (active) setLoading(false) })
    }, 250)
    return () => { active = false; clearTimeout(t) }
  }, [input, url])

  return (
    <Autocomplete
      multiple size="small" fullWidth
      inputValue={input}
      value={value || []}
      options={options}
      loading={loading}
      filterSelectedOptions
      getOptionLabel={oppLabel}
      getOptionSelected={(a, b) => a.OpportunityID === b.OpportunityID}
      filterOptions={x => x}  // server-side search; don't re-filter locally
      onInputChange={(e, v, reason) => { if (reason !== 'reset') setInput(v) }}
      onChange={(e, v) => { onChange(v); setInput('') }}
      renderInput={params => (
        <TextField
          {...params} variant="outlined" label="Aspire jobs (opportunities)"
          placeholder="Search by opportunity #, name, or property…"
          InputProps={{
            ...params.InputProps,
            endAdornment: <>{loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>
          }}
        />
      )}
      renderTags={(vals, getTagProps) =>
        vals.map((o, i) => (
          <Chip size="small" key={o.OpportunityID} label={`#${o.OpportunityNumber || o.OpportunityID}`} {...getTagProps({ index: i })} />
        ))
      }
    />
  )
}

/* ── per-crew % attribution of visits across invoice groups ────── */
// savedAllocations: [{ routeId, splits: [{ groupId, percent }] }]
// Local edit state: { [routeId]: { [groupId]: percentString } }
// Generic % matrix: rows × invoice groups, each row totals 100% (warned).
// rows: [{ key, primary, secondary }]. rowColLabel: header for the row column.
// heading: table title. savedByKey: { [rowKey]: { [groupId]: percent } }.
// buildAllocation(row, splits): shape one saved allocation object.
const AllocationMatrix = ({ rows, groups, rowColLabel, heading, savedByKey, buildAllocation, onSave, emptyRowsText, rowAction, locked }) => {
  const seed = useMemo(() => {
    const m = {}
    for (const k of Object.keys(savedByKey || {})) {
      m[k] = {}
      for (const gid of Object.keys(savedByKey[k])) m[k][gid] = String(savedByKey[k][gid])
    }
    return m
  }, [savedByKey])
  const [edits, setEdits] = useState(seed)
  useEffect(() => { setEdits(seed) }, [seed])

  if (!groups.length) {
    return <Typography variant="body2" color="textSecondary">Create invoice groups first, then attribute to them.</Typography>
  }
  if (!rows.length) {
    return <Typography variant="body2" color="textSecondary">{emptyRowsText || 'Nothing to attribute.'}</Typography>
  }

  const cellVal = (rowKey, groupId) => (edits[rowKey] && edits[rowKey][groupId]) || ''
  const rowTotal = (rowKey) => groups.reduce((s, g) => s + (Number(cellVal(rowKey, g._id)) || 0), 0)
  const setCell = (rowKey, groupId, v) =>
    setEdits(e => ({ ...e, [rowKey]: { ...(e[rowKey] || {}), [groupId]: v } }))

  // Persist the whole matrix — the API replaces allocations wholesale.
  const persist = () => {
    const allocations = rows.map(r => {
      const splits = groups
        .map(g => ({ groupId: g._id, percent: Number(cellVal(r.key, g._id)) || 0 }))
        .filter(s => s.percent > 0)
      return buildAllocation(r, splits)
    }).filter(a => a.splits.length)
    onSave(allocations)
  }

  const stickyTh = { padding: '6px 8px', fontWeight: 600, position: 'sticky', top: 0, background: '#fff', zIndex: 1, boxShadow: 'inset 0 -1px 0 #ddd' }
  return (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom>{heading}</Typography>
      <Box style={{ overflow: 'auto', maxHeight: 420 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 480 }}>
        <thead>
          <tr>
            <th style={{ ...stickyTh, textAlign: 'left' }}>{rowColLabel}</th>
            {groups.map(g => (
              <th key={g._id} style={{ ...stickyTh, whiteSpace: 'nowrap' }}>{g.name}</th>
            ))}
            <th style={stickyTh}>Total</th>
            {rowAction && <th style={stickyTh} />}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const total = rowTotal(r.key)
            const remaining = r.remaining != null ? r.remaining : 100
            const fullyInvoiced = remaining <= 0
            const rowLocked = locked || fullyInvoiced
            // target for a complete allocation is the remaining %, not always 100
            const ok = Math.round(total) === Math.round(remaining) && remaining > 0
            return (
              <tr key={r.key} style={{ borderTop: '1px solid #eee', opacity: fullyInvoiced ? 0.5 : 1 }}>
                <td style={{ padding: '4px 8px' }}>
                  <Typography variant="body2">{r.primary}{fullyInvoiced && ' 🔒'}</Typography>
                  {r.secondary && <Typography variant="caption" color="textSecondary">{r.secondary}</Typography>}
                </td>
                {groups.map(g => (
                  <td key={g._id} style={{ padding: '4px 8px', textAlign: 'center' }}>
                    <TextField
                      size="small" type="number" variant="outlined"
                      disabled={rowLocked}
                      value={cellVal(r.key, g._id)}
                      onChange={e => setCell(r.key, g._id, e.target.value)}
                      onBlur={persist}
                      inputProps={{ min: 0, max: 100, style: { width: 52, textAlign: 'right', padding: '6px 8px' } }}
                    />
                  </td>
                ))}
                <td style={{ padding: '4px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {fullyInvoiced
                    ? <Typography variant="body2" color="textSecondary">fully invoiced</Typography>
                    : <Typography variant="body2" style={{ color: ok ? '#4caf50' : '#d32f2f', fontWeight: 600 }}>
                        {total}%{remaining < 100 ? ` / ${remaining}% avail` : (ok ? '' : ' ≠100')}
                      </Typography>}
                </td>
                {rowAction && <td style={{ padding: '4px 8px', textAlign: 'center' }}>{rowAction(r)}</td>}
              </tr>
            )
          })}
        </tbody>
      </table>
      </Box>
    </Box>
  )
}

/* ── work ticket visits for the draft's linked opportunities ───── */
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—')
// Aspire stores Arizona wall-clock values with a fake Z suffix. Render the UTC
// clock components so 06:18Z remains the crew's 6:18 AM, not 11:18 PM Phoenix.
const fmtAspireTime = (d) => d
  ? new Date(d).toLocaleTimeString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' })
  : null
const laborAdjustmentKey = (monthKey, laborPool) => `${monthKey}|${laborPool}`
const laborMonthLabel = (monthKey) => {
  const [year, month] = String(monthKey || '').split('-').map(Number)
  if (!year || !month) return monthKey
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric'
  })
}

// Deep links into the Aspire web app so users can inspect the source record.
const ASPIRE_APP = 'https://cloud.youraspire.com/app'
const aspireLink = {
  receipt: (id) => `${ASPIRE_APP}/purchasing/receipts/details/${id}`,
  workTicket: (id) => `${ASPIRE_APP}/worktickets/details/${id}`,
  visit: (id) => `${ASPIRE_APP}/worktickets/visit/${id}`
}
// Small "↗ Aspire" external link, or nothing when the id is missing.
const AspireLink = ({ href, label }) => href
  ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, marginLeft: 6 }}>{label} ↗</a>
  : null

const VisitsPanel = ({ url, draftId, stageId, reloadKey, hasOpportunities, groups, savedAllocations, onSaveAllocations, onSaveVisits, onSavePersonLaborPool, onSaveLaborHourAdjustments, locked, onCrewsLoaded, hoursOverride, onSaveHoursOverride, laborPoolRates, onSaveLaborPoolRates }) => {
  const [visits, setVisits] = useState(null)
  const [crews, setCrews] = useState([])
  const [people, setPeople] = useState([])
  const [laborPools, setLaborPools] = useState([])
  const [laborSummaryPools, setLaborSummaryPools] = useState([])
  const [laborLogs, setLaborLogs] = useState([])
  const [laborAdjustmentEdits, setLaborAdjustmentEdits] = useState({})
  const [manualLaborRows, setManualLaborRows] = useState([])
  const [newLaborMonth, setNewLaborMonth] = useState('')
  const [newLaborPool, setNewLaborPool] = useState('General Labor')
  const [error, setError] = useState(null)
  const [showVisits, setShowVisits] = useState(false)
  const [selectionConfigured, setSelectionConfigured] = useState(false)
  const [selectedVisitIds, setSelectedVisitIds] = useState(new Set())
  const [savingVisits, setSavingVisits] = useState(false)
  const persistedVisitIdsRef = useRef(new Set())
  const currentStageIdRef = useRef(stageId)
  const laborAdjustmentSaveRef = useRef(Promise.resolve())
  const laborTotal = people.reduce((s, person) => s + (Number(person.laborCost) || 0), 0)
  const laborPoolSummary = useMemo(() => {
    const byPool = new Map()
    for (const person of people) {
      const pool = person.laborPool || 'Unassigned'
      const current = byPool.get(pool) || { laborPool: pool, hours: 0, cost: 0 }
      current.hours += Number(person.laborHours) || 0
      current.cost += Number(person.laborCost) || 0
      byPool.set(pool, current)
    }
    return [...byPool.values()].sort((a, b) => b.hours - a.hours || a.laborPool.localeCompare(b.laborPool))
  }, [people])
  const laborPoolSummaryByName = useMemo(
    () => new Map(laborPoolSummary.map(summary => [summary.laborPool, summary])),
    [laborPoolSummary]
  )
  const displayedLaborLogs = useMemo(() => {
    const byMonth = new Map((laborLogs || []).map(month => [
      month.monthKey,
      { ...month, pools: (month.pools || []).map(pool => ({ ...pool })) }
    ]))
    for (const key of manualLaborRows) {
      const separator = key.indexOf('|')
      const monthKey = key.slice(0, separator)
      const laborPool = key.slice(separator + 1)
      if (!monthKey || !laborPool) continue
      const month = byMonth.get(monthKey) || {
        monthKey,
        monthLabel: laborMonthLabel(monthKey),
        sourceHours: 0,
        adjustmentHours: 0,
        totalHours: 0,
        pools: []
      }
      if (!month.pools.some(pool => pool.laborPool === laborPool)) {
        month.pools.push({
          laborPool,
          sourceHours: 0,
          adjustmentHours: 0,
          totalHours: 0
        })
      }
      byMonth.set(monthKey, month)
    }
    const order = new Map(laborSummaryPools.map((pool, index) => [pool, index]))
    return [...byMonth.values()]
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .map(month => ({
        ...month,
        pools: [...month.pools].sort((a, b) =>
          (order.get(a.laborPool) ?? 999) - (order.get(b.laborPool) ?? 999) ||
          a.laborPool.localeCompare(b.laborPool))
      }))
  }, [laborLogs, laborSummaryPools, manualLaborRows])
  const laborSourceHoursByKey = useMemo(() => {
    const hours = new Map()
    for (const month of displayedLaborLogs) {
      for (const pool of month.pools || []) {
        hours.set(laborAdjustmentKey(month.monthKey, pool.laborPool), Number(pool.sourceHours) || 0)
      }
    }
    return hours
  }, [displayedLaborLogs])
  useEffect(() => {
    currentStageIdRef.current = stageId
    setSavingVisits(false)
  }, [stageId])

  const savedRatesByPool = useMemo(() =>
    (laborPoolRates || []).reduce((map, row) => {
      map[row.laborPool] = row.rate || ''
      return map
    }, {}), [laborPoolRates])
  const [poolRateEdits, setPoolRateEdits] = useState({})
  useEffect(() => { setPoolRateEdits(savedRatesByPool) }, [savedRatesByPool])
  const loadLaborSummary = useCallback((data) => {
    const logs = Array.isArray(data?.laborLogs) ? data.laborLogs : []
    const edits = {}
    for (const month of logs) {
      for (const pool of month.pools || []) {
        if (Number(pool.adjustmentHours)) {
          edits[laborAdjustmentKey(month.monthKey, pool.laborPool)] = String(pool.adjustmentHours)
        }
      }
    }
    setLaborLogs(logs)
    setLaborAdjustmentEdits(edits)
    setManualLaborRows([])
  }, [])
  useEffect(() => {
    if (!hasOpportunities || !stageId) {
      setVisits([])
      setCrews([])
      setPeople([])
      setLaborPools([])
      setLaborSummaryPools([])
      setLaborLogs([])
      setLaborAdjustmentEdits({})
      setManualLaborRows([])
      setSelectedVisitIds(new Set())
      persistedVisitIdsRef.current = new Set()
      onCrewsLoaded && onCrewsLoaded([])
      return
    }
    let active = true
    setVisits(null); setError(null)
    axios.get(`${url}/api/bid-estimation/invoice-grouping/drafts/${draftId}/visits`, { params: { stageId } })
      .then(res => {
        if (!active) return
        const vv = res.data.visits || []
        const cc = res.data.crews || []
        const pp = res.data.people || []
        setVisits(vv)
        setCrews(cc)
        setPeople(pp)
        setLaborPools(Array.isArray(res.data.laborPools) ? res.data.laborPools : [])
        setLaborSummaryPools(Array.isArray(res.data.laborSummaryPools) ? res.data.laborSummaryPools : [])
        loadLaborSummary(res.data)
        setSelectionConfigured(res.data.visitSelectionConfigured === true)
        const selectedIds = new Set((res.data.selectedVisitIds || []).map(Number))
        setSelectedVisitIds(selectedIds)
        persistedVisitIdsRef.current = selectedIds
        onCrewsLoaded && onCrewsLoaded(cc)
      })
      .catch(e => { if (active) setError(e.response?.data?.error || e.message) })
    return () => { active = false }
  }, [url, draftId, stageId, reloadKey, hasOpportunities, onCrewsLoaded, loadLaborSummary])

  if (!hasOpportunities || !stageId) return null
  const configuredFallback = Number.isFinite(hoursOverride) && hoursOverride > 0
  const missingHoursFallback = configuredFallback ? hoursOverride : 8
  const selectedVisits = (visits || []).filter(v => selectedVisitIds.has(Number(v.WorkTicketVisitID)))
  const selectedLaborHours = selectedVisits.reduce((sum, visit) => sum + (Number(visit.laborHours) || 0), 0)
  const selectedOnSiteHours = selectedVisits.reduce((sum, visit) => sum + (Number(visit.onSiteLaborHours) || 0), 0)
  const selectedDriveHours = selectedVisits.reduce((sum, visit) => sum + (Number(visit.driveLaborHours) || 0), 0)
  const selectionDirty = selectedVisitIds.size !== persistedVisitIdsRef.current.size ||
    [...selectedVisitIds].some(id => !persistedVisitIdsRef.current.has(Number(id)))
  const replaceVisitSelection = (nextSet) => {
    if (locked || savingVisits) return
    setSelectedVisitIds(new Set([...nextSet].map(Number)))
  }
  const refreshVisitsSilently = (requestedStageId) =>
    axios.get(`${url}/api/bid-estimation/invoice-grouping/drafts/${draftId}/visits`, {
      params: { stageId: requestedStageId }
    }).then(res => {
      if (currentStageIdRef.current !== requestedStageId) return
      const vv = res.data.visits || []
      const cc = res.data.crews || []
      const pp = res.data.people || []
      const selectedIds = new Set((res.data.selectedVisitIds || []).map(Number))
      setVisits(vv)
      setCrews(cc)
      setPeople(pp)
      setLaborPools(Array.isArray(res.data.laborPools) ? res.data.laborPools : [])
      setLaborSummaryPools(Array.isArray(res.data.laborSummaryPools) ? res.data.laborSummaryPools : [])
      loadLaborSummary(res.data)
      setSelectionConfigured(res.data.visitSelectionConfigured === true)
      setSelectedVisitIds(selectedIds)
      persistedVisitIdsRef.current = selectedIds
      onCrewsLoaded && onCrewsLoaded(cc)
    }).catch(e => {
      if (currentStageIdRef.current === requestedStageId) {
        setError(e.response?.data?.error || e.message)
      }
    })
  const persistLaborPoolRates = () => {
    const requestedStageId = stageId
    const next = laborPools
      .map(laborPool => ({ laborPool, rate: Number(poolRateEdits[laborPool]) || 0 }))
      .filter(row => row.rate > 0)
    Promise.resolve(onSaveLaborPoolRates(next))
      .then(() => refreshVisitsSilently(requestedStageId))
      .catch(() => setPoolRateEdits(savedRatesByPool))
  }
  const persistLaborHourAdjustments = () => {
    if (locked) return
    const normalizedEdits = Object.entries(laborAdjustmentEdits).reduce((values, [key, value]) => {
      if (value === '') return values
      const sourceHours = laborSourceHoursByKey.get(key) || 0
      values[key] = String(Math.max(-sourceHours, Number(value) || 0))
      return values
    }, {})
    setLaborAdjustmentEdits(normalizedEdits)
    const next = Object.entries(normalizedEdits)
      .map(([key, value]) => {
        const separator = key.indexOf('|')
        return {
          monthKey: key.slice(0, separator),
          laborPool: key.slice(separator + 1),
          hoursDelta: Number(value) || 0
        }
      })
      .filter(row => row.monthKey && row.laborPool && row.hoursDelta !== 0)
    const requestedStageId = stageId
    laborAdjustmentSaveRef.current = laborAdjustmentSaveRef.current
      .catch(() => {})
      .then(() => onSaveLaborHourAdjustments(next))
      .catch(saveError => {
        if (currentStageIdRef.current === requestedStageId) {
          setError(saveError.response?.data?.error || saveError.message)
        }
      })
  }
  const addLaborSummaryRow = () => {
    if (!newLaborMonth || !newLaborPool) return
    const key = laborAdjustmentKey(newLaborMonth, newLaborPool)
    setManualLaborRows(current => current.includes(key) ? current : [...current, key])
    setNewLaborMonth('')
  }
  const persistVisitSelection = () => {
    if (locked || savingVisits || !selectionDirty) return
    const requestedStageId = stageId
    const previous = new Set(persistedVisitIdsRef.current)
    const next = new Set([...selectedVisitIds].map(Number))
    setSavingVisits(true)
    Promise.resolve(onSaveVisits([...next]))
      .then(() => {
        persistedVisitIdsRef.current = next
        setSelectionConfigured(true)
        return refreshVisitsSilently(requestedStageId)
      })
      .catch(() => {
        if (currentStageIdRef.current === requestedStageId) setSelectedVisitIds(previous)
      })
      .then(() => {
        if (currentStageIdRef.current === requestedStageId) setSavingVisits(false)
      })
  }
  const saveLaborPool = (contactId, laborPool) => {
    const previousPool = people.find(person => Number(person.contactId) === Number(contactId))?.laborPool || null
    const nextPool = laborPool || null
    const optimistic = people.map(person =>
      Number(person.contactId) === Number(contactId) ? { ...person, laborPool: nextPool } : person)
    setPeople(optimistic)
    Promise.resolve(onSavePersonLaborPool(contactId, nextPool))
      .then(() => refreshVisitsSilently(stageId))
      .catch(() => {
        setPeople(optimistic.map(person =>
          Number(person.contactId) === Number(contactId) ? { ...person, laborPool: previousPool } : person))
      })
  }
  const toggleVisit = (visitId) => {
    // A legacy stage initially represents "all visits." On its first explicit
    // edit, discard visits already owned by another approved invoice so the
    // saved selection cannot accidentally overlap them.
    const availableIds = new Set((visits || [])
      .filter(visit => !visit.lockedByApprovedInvoice)
      .map(visit => Number(visit.WorkTicketVisitID)))
    const next = new Set([...selectedVisitIds].filter(id => availableIds.has(Number(id))))
    if (next.has(Number(visitId))) next.delete(Number(visitId))
    else next.add(Number(visitId))
    replaceVisitSelection(next)
  }
  return (
    <Card style={{ marginBottom: 16 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
          <Typography variant="h6">
            Work Ticket Visits{visits ? ` (${visits.length})` : ''}
            {visits && <span style={{ color: '#1565c0', fontWeight: 600, marginLeft: 8 }}>· {selectedVisitIds.size} selected / {selectedLaborHours.toFixed(1)}h</span>}
            {visits && !!visits.length && <span style={{ color: '#6a1b9a', fontWeight: 600, marginLeft: 8 }}>· Labor {money(laborTotal)}</span>}
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Chip
              size="small"
              label={`Missing time → ${missingHoursFallback}h`}
              style={{ backgroundColor: '#6a1b9a', color: '#fff' }}
            />
            {configuredFallback && hoursOverride !== 8 && (
              <Button size="small" onClick={() => onSaveHoursOverride(null)}>Reset to 8h</Button>
            )}
          </Box>
        </Box>
        <Typography variant="caption" color="textSecondary">
          Uses positive Aspire hours first, then clock-in/out elapsed time. Real drive hours are included; only a non-drive crew entry with neither falls back to {missingHoursFallback}h.
        </Typography>
        {visits && (
          <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
            This invoice: {selectedOnSiteHours.toFixed(1)}h on-site + {selectedDriveHours.toFixed(1)}h drive = {selectedLaborHours.toFixed(1)}h
            {!selectionConfigured && !selectionDirty ? ' · legacy stage currently includes all visits; change any checkbox to save an explicit selection' : ''}
            {selectionDirty ? ' · selection will save when you leave the visit list' : ''}
          </Typography>
        )}
        {visits && (selectionConfigured || selectionDirty) && selectedVisitIds.size === 0 && (
          <Typography variant="body2" style={{ color: '#b26a00', marginTop: 8 }}>
            No visits selected for this invoice yet.
          </Typography>
        )}
        {error && <Typography variant="body2" color="error">{error}</Typography>}
        {visits === null && <Box mt={1}><CircularProgress size={20} /></Box>}
        {visits && !visits.length && (
          <Typography variant="body2" color="textSecondary">No visits found for the linked opportunities.</Typography>
        )}
        {visits && (
          <Box mt={2} p={2} style={{ border: '1px solid #ddd', borderRadius: 4, background: '#fafafa' }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Labor Summary — current invoice</Typography>
            <Typography variant="caption" color="textSecondary">
              Reconcile Aspire with the paper log using a positive or negative adjustment. Synced visits remain unchanged.
            </Typography>
            {displayedLaborLogs.map(month => (
              <Box key={month.monthKey} mt={2}>
                <Typography variant="subtitle2">{month.monthLabel || laborMonthLabel(month.monthKey)}</Typography>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 620 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Labor pool</th>
                      <th style={{ padding: '4px 8px' }}>Aspire hrs</th>
                      <th style={{ padding: '4px 8px' }}>Paper adjustment (+/−)</th>
                      <th style={{ padding: '4px 8px' }}>Labor Summary hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(month.pools || []).map(pool => {
                      const key = laborAdjustmentKey(month.monthKey, pool.laborPool)
                      const sourceHours = Number(pool.sourceHours) || 0
                      const adjustment = Math.max(-sourceHours, Number(laborAdjustmentEdits[key]) || 0)
                      const adjustedHours = Math.max(0, sourceHours + adjustment)
                      return (
                        <tr key={key} style={{ borderTop: '1px solid #e5e5e5' }}>
                          <td style={{ padding: '4px 8px' }}>{pool.laborPool}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'center' }}>{sourceHours.toFixed(2)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                            <TextField
                              size="small"
                              type="number"
                              variant="outlined"
                              placeholder="0.00"
                              disabled={locked}
                              value={laborAdjustmentEdits[key] ?? ''}
                              onChange={event => setLaborAdjustmentEdits(current => ({
                                ...current,
                                [key]: event.target.value
                              }))}
                              onBlur={persistLaborHourAdjustments}
                              inputProps={{
                                min: -sourceHours,
                                step: '0.25',
                                style: { width: 92, textAlign: 'right', padding: '6px 8px' }
                              }}
                            />
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600 }}>
                            {adjustedHours.toFixed(2)}
                          </td>
                        </tr>
                      )
                    })}
                    <tr style={{ borderTop: '1px solid #bbb' }}>
                      <td style={{ padding: '5px 8px', fontWeight: 600 }}>Monthly Total</td>
                      <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 600 }}>
                        {(month.pools || []).reduce((sum, pool) => sum + (Number(pool.sourceHours) || 0), 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 600 }}>
                        {(month.pools || []).reduce((sum, pool) => {
                          const key = laborAdjustmentKey(month.monthKey, pool.laborPool)
                          const sourceHours = Number(pool.sourceHours) || 0
                          return sum + Math.max(-sourceHours, Number(laborAdjustmentEdits[key]) || 0)
                        }, 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 600 }}>
                        {(month.pools || []).reduce((sum, pool) => {
                          const key = laborAdjustmentKey(month.monthKey, pool.laborPool)
                          return sum + Math.max(0,
                            (Number(pool.sourceHours) || 0) + (Number(laborAdjustmentEdits[key]) || 0))
                        }, 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Box>
            ))}
            {!displayedLaborLogs.length && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                No selected visit hours yet. Add a month and labor pool to enter a paper-log correction.
              </Typography>
            )}
            {!locked && (
              <Box display="flex" alignItems="center" flexWrap="wrap" mt={2} style={{ gap: 8 }}>
                <TextField
                  size="small"
                  type="month"
                  variant="outlined"
                  label="Month"
                  value={newLaborMonth}
                  onChange={event => setNewLaborMonth(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label="Labor pool"
                  value={newLaborPool}
                  onChange={event => setNewLaborPool(event.target.value)}
                  style={{ minWidth: 170 }}
                >
                  {laborSummaryPools.map(pool => (
                    <MenuItem key={pool} value={pool}>{pool}</MenuItem>
                  ))}
                </TextField>
                <Button size="small" variant="outlined" onClick={addLaborSummaryRow} disabled={!newLaborMonth}>
                  Add month/pool
                </Button>
              </Box>
            )}
          </Box>
        )}
        {visits && !!visits.length && (
          <Box mt={2} style={{ overflowX: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>Individual labor pools</Typography>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 680 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>Team member</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600 }}>Labor pool</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600 }}>Hours</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600 }}>Pool $/hr</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600 }}>Labor</th>
                </tr>
              </thead>
              <tbody>
                {people.map(person => (
                  <tr key={person.contactId || person.contactName} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '4px 8px' }}>
                      {person.contactName || `Contact ${person.contactId}`}
                      {!!person.routeNames?.length && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {person.routeNames.join(', ')}
                        </Typography>
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      <TextField
                        select
                        size="small"
                        variant="outlined"
                        value={person.laborPool === 'Unassigned' ? '' : (person.laborPool || '')}
                        disabled={!person.contactId}
                        onChange={event => saveLaborPool(person.contactId, event.target.value)}
                        style={{ minWidth: 150 }}
                      >
                        <MenuItem value=""><em>Unassigned</em></MenuItem>
                        {laborPools.map(pool => <MenuItem key={pool} value={pool}>{pool}</MenuItem>)}
                      </TextField>
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {(person.laborHours || 0).toFixed(1)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {person.visitCount || 0} selected {person.visitCount === 1 ? 'visit' : 'visits'}
                      </Typography>
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      {money(person.laborPoolRate || 0)}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {money(person.laborCost)}
                      {person.driveLaborHours > 0 && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          includes {person.driveLaborHours.toFixed(1)}h drive
                        </Typography>
                      )}
                      {person.fallbackCrewVisitCount > 0 && (
                        <Typography variant="caption" style={{ display: 'block', color: '#b26a00' }}>
                          {person.fallbackCrewVisitCount} {person.fallbackCrewVisitCount === 1 ? 'visit' : 'visits'} used the {missingHoursFallback}h fallback
                        </Typography>
                      )}
                      {person.missingDriveHoursEntryCount > 0 && (
                        <Typography variant="caption" style={{ display: 'block', color: '#b26a00' }}>
                          {person.missingDriveHoursEntryCount} drive {person.missingDriveHoursEntryCount === 1 ? 'entry has' : 'entries have'} no usable duration
                        </Typography>
                      )}
                    </td>
                  </tr>
                ))}
                {!people.length && (
                  <tr><td colSpan={5} style={{ padding: '8px', color: '#777' }}>No individuals on the selected visits.</td></tr>
                )}
              </tbody>
            </table>
            <Box display="flex" flexWrap="wrap" mt={1} style={{ gap: 6 }}>
              {laborPoolSummary.map(pool => (
                <Chip
                  key={pool.laborPool}
                  size="small"
                  variant={pool.laborPool === 'Unassigned' ? 'outlined' : 'default'}
                  label={`${pool.laborPool}: ${pool.hours.toFixed(1)}h · ${money(pool.cost)}`}
                  style={pool.laborPool === 'Unassigned' ? { color: '#b26a00', borderColor: '#b26a00' } : undefined}
                />
              ))}
            </Box>
            <Typography variant="caption" color="textSecondary">
              Labor pools persist on each Aspire contact and follow that person across crews, routes, and future projects.
            </Typography>

            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Labor pool rates ($/hr)</Typography>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 560 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>Labor pool</th>
                    <th style={{ padding: '4px 8px', fontWeight: 600 }}>Selected hours</th>
                    <th style={{ padding: '4px 8px', fontWeight: 600 }}>Rate $/hr</th>
                    <th style={{ padding: '4px 8px', fontWeight: 600 }}>Labor</th>
                  </tr>
                </thead>
                <tbody>
                  {laborPools.map(laborPool => {
                    const summary = laborPoolSummaryByName.get(laborPool)
                    const selectedHours = Number(summary?.hours) || 0
                    const rate = Number(poolRateEdits[laborPool]) || 0
                    return (
                    <tr key={laborPool} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: '4px 8px' }}>{laborPool}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>{selectedHours.toFixed(1)}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                        <TextField
                          size="small" type="number" variant="outlined"
                          placeholder="0.00"
                          value={poolRateEdits[laborPool] ?? ''}
                          onChange={event => setPoolRateEdits(current => ({
                            ...current,
                            [laborPool]: event.target.value
                          }))}
                          onBlur={persistLaborPoolRates}
                          inputProps={{ min: 0, step: '0.01', style: { width: 80, textAlign: 'right', padding: '6px 8px' } }}
                        />
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>{money(selectedHours * rate)}</td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
              <Typography variant="caption" color="textSecondary">
                Each person’s actual hours use the rate assigned to their labor pool. Unassigned people calculate at $0 until classified.
              </Typography>
            </Box>
          </Box>
        )}
        {visits && !!visits.length && (
          <AllocationMatrix
            groups={groups}
            locked={locked}
            rowColLabel="Crew (route)"
            heading="Attribute each crew’s visits to invoice groups (%)"
            emptyRowsText="No crews to attribute."
            rows={crews.map(c => ({
              key: String(c.routeId),
              routeId: c.routeId, routeName: c.routeName,
              remaining: c.remainingPercent != null ? c.remainingPercent : 100,
              primary: c.routeName || `Route ${c.routeId}`,
              secondary: `${c.visitCount} visit${c.visitCount === 1 ? '' : 's'}${(c.invoicedPercent || 0) > 0 ? ` · ${c.invoicedPercent}% invoiced` : ''}`
            }))}
            savedByKey={(savedAllocations || []).reduce((m, a) => {
              m[String(a.routeId)] = (a.splits || []).reduce((mm, s) => { mm[String(s.groupId)] = s.percent; return mm }, {})
              return m
            }, {})}
            buildAllocation={(r, splits) => ({ routeId: r.routeId, routeName: r.routeName, splits })}
            onSave={onSaveAllocations}
          />
        )}
        {visits && !!visits.length && (
          <Box mt={2}>
            <Button size="small" variant="outlined" onClick={() => setShowVisits(v => !v)}>
              {showVisits ? 'Hide visit selection' : `Select visits for this invoice (${selectedVisitIds.size}/${visits.length})`}
            </Button>
          </Box>
        )}
        {visits && !!visits.length && showVisits && (
          <Box
            mt={1}
            onBlur={event => {
              if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) persistVisitSelection()
            }}
          >
            <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
              <Button
                size="small"
                disabled={locked || savingVisits}
                onClick={() => replaceVisitSelection(new Set(visits.filter(v => !v.lockedByApprovedInvoice).map(v => v.WorkTicketVisitID)))}
              >
                Select all available
              </Button>
              <Button size="small" disabled={locked || savingVisits || selectedVisitIds.size === 0} onClick={() => replaceVisitSelection(new Set())}>
                Clear
              </Button>
              {savingVisits && <CircularProgress size={16} />}
              <Typography variant="caption" color="textSecondary">
                Checked visits supply this invoice’s labor hours. Changes save when you leave this list.
              </Typography>
            </Box>
            <Box style={{ maxHeight: 480, overflowY: 'auto' }}>
              <List dense>
                {visits.map(v => {
                  const visitId = Number(v.WorkTicketVisitID)
                  const visitLocked = v.lockedByApprovedInvoice
                  return (
                    <ListItem key={v.WorkTicketVisitID || `${v.WorkTicketID}-${v.ScheduledDate}`} disableGutters>
                      <ListItemIcon style={{ minWidth: 38 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedVisitIds.has(visitId)}
                          disabled={locked || savingVisits || visitLocked}
                          onChange={() => toggleVisit(visitId)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={`WT #${v.WorkTicketNumber || v.WorkTicketID || '—'} · ${fmtDate(v.ScheduledDate)} · ${(Number(v.laborHours) || 0).toFixed(1)}h`}
                        secondary={
                          [
                            v.RouteName,
                            `${(Number(v.onSiteLaborHours) || 0).toFixed(1)}h on-site`,
                            (Number(v.driveLaborHours) || 0) > 0 ? `${Number(v.driveLaborHours).toFixed(1)}h drive` : null,
                            v.WorkTicketStatusName,
                            v.actualCrewCount ? `${v.actualCrewCount} crew` : null,
                            v.ArrivalTime ? `in ${fmtAspireTime(v.ArrivalTime)}` : null,
                            v.DepartureTime ? `out ${fmtAspireTime(v.DepartureTime)}` : null
                          ].filter(Boolean).join(' · ') || `Opp #${v.OpportunityID}`
                        }
                      />
                      {visitLocked && <Chip size="small" label="Already approved" />}
                      <AspireLink href={v.WorkTicketID ? aspireLink.workTicket(v.WorkTicketID) : null} label="WT" />
                      <AspireLink href={v.WorkTicketVisitID ? aspireLink.visit(v.WorkTicketVisitID) : null} label="Visit" />
                    </ListItem>
                  )
                })}
              </List>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/* ── full-screen receipt review: PDF ‹ items + allocation, cycle ── */
const ReceiptReviewWindow = ({ url, draftId, receipts, startIndex, groups, savedByReceiptId, onSaveOne, onClose }) => {
  const [unattributedOnly, setUnattributedOnly] = useState(false)
  const [atts, setAtts] = useState(null)
  const [activeAtt, setActiveAtt] = useState(null)
  const [attError, setAttError] = useState(null)
  const base = `${url}/api/bid-estimation/invoice-grouping/drafts/${draftId}`

  // A receipt is "attributed" if it has any saved allocation split.
  const isAttributed = useCallback((r) => {
    const saved = savedByReceiptId[r.ReceiptID]
    return saved && Object.values(saved).some(v => Number(v) > 0)
  }, [savedByReceiptId])

  // The list we page through — full, or filtered to unattributed.
  const view = useMemo(
    () => unattributedOnly ? receipts.filter(r => !isAttributed(r)) : receipts,
    [receipts, unattributedOnly, isAttributed]
  )

  // Track the current receipt by ID so toggling the filter keeps our place.
  const [activeReceiptId, setActiveReceiptId] = useState(receipts[startIndex || 0]?.ReceiptID)
  // Keep the active receipt valid as the view changes (e.g. after filtering).
  useEffect(() => {
    if (!view.length) return
    if (!view.some(r => r.ReceiptID === activeReceiptId)) setActiveReceiptId(view[0].ReceiptID)
  }, [view, activeReceiptId])

  const idx = view.findIndex(r => r.ReceiptID === activeReceiptId)
  const receipt = view[idx] || null

  // load this receipt's attachments whenever the index changes
  useEffect(() => {
    if (!receipt) return
    let active = true
    setAtts(null); setActiveAtt(null); setAttError(null)
    axios.get(`${base}/receipts/${receipt.ReceiptID}/attachments`)
      .then(res => { if (active) { const a = res.data.attachments || []; setAtts(a); setActiveAtt(a[0]?.AttachmentID || null) } })
      .catch(e => { if (active) setAttError(e.response?.data?.error || e.message) })
    return () => { active = false }
  }, [base, receipt])

  // local % edits for the current receipt, seeded from saved allocation
  const [pct, setPct] = useState({})
  useEffect(() => {
    setPct(receipt ? { ...(savedByReceiptId[receipt.ReceiptID] || {}) } : {})
  }, [receipt, savedByReceiptId])

  const fileUrl = (receipt && activeAtt) ? `${base}/attachments/${activeAtt}/file` : null
  const activeObj = (atts || []).find(a => a.AttachmentID === activeAtt)
  const isImage = /\.(jpe?g|png|gif|webp|tif?f|bmp|heic)$/i.test(activeObj?.FileExtension || activeObj?.OriginalFileName || activeObj?.AttachmentName || '')
  const total = groups.reduce((s, g) => s + (Number(pct[g._id]) || 0), 0)
  const ok = Math.round(total) === 100
  const unattributedCount = receipts.filter(r => !isAttributed(r)).length

  const persist = () => {
    if (!receipt) return
    const splits = groups
      .map(g => ({ groupId: g._id, percent: Number(pct[g._id]) || 0 }))
      .filter(s => s.percent > 0)
    onSaveOne(receipt, splits)
  }
  const go = (delta) => {
    persist()
    const next = Math.min(view.length - 1, Math.max(0, idx + delta))
    if (view[next]) setActiveReceiptId(view[next].ReceiptID)
  }
  const filterToggle = (
    <FormControlLabel
      control={<Switch size="small" color="primary" checked={unattributedOnly} onChange={e => setUnattributedOnly(e.target.checked)} />}
      label={<Typography variant="caption">Unattributed only ({unattributedCount})</Typography>}
      style={{ marginLeft: 0 }}
    />
  )

  return (
    <Dialog open fullScreen onClose={() => { persist(); onClose() }}>
      {/* top bar */}
      <Box display="flex" alignItems="center" px={2} py={1} style={{ gap: 12, borderBottom: '1px solid #ddd' }}>
        <Button onClick={() => { persist(); onClose() }}>✕ Close</Button>
        <Typography variant="h6" style={{ flexGrow: 1 }}>
          {receipt ? `Receipt #${receipt.ReceiptNumber || receipt.ReceiptID} · ${money(receipt.ReceiptTotalCost)}` : 'Receipts'}
        </Typography>
        {filterToggle}
        <Typography variant="body2" color="textSecondary">{view.length ? `${idx + 1} of ${view.length}` : '0'}</Typography>
        <Button disabled={idx <= 0} onClick={() => go(-1)}>← Prev</Button>
        <Button disabled={idx >= view.length - 1} onClick={() => go(1)}>Next →</Button>
      </Box>

      {!receipt ? (
        <Box p={6} textAlign="center">
          <Typography variant="h6" color="textSecondary">🎉 All receipts are attributed.</Typography>
          <Typography variant="body2" color="textSecondary">Turn off “Unattributed only” to review them.</Typography>
        </Box>
      ) : (
      <Box display="flex" style={{ height: 'calc(100vh - 57px)' }}>
        {/* left: PDF */}
        <Box style={{ flex: 3, minWidth: 0, borderRight: '1px solid #ddd', display: 'flex', flexDirection: 'column' }}>
          {attError && <Box p={2}><Typography color="error" variant="body2">{attError}</Typography></Box>}
          {atts === null && !attError && <Box textAlign="center" mt={4}><CircularProgress /></Box>}
          {atts && !atts.length && <Box p={2}><Typography variant="body2" color="textSecondary">No attachment on this receipt.</Typography></Box>}
          {atts && atts.length > 1 && (
            <Box p={1} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
              {atts.map(a => (
                <Chip key={a.AttachmentID} size="small"
                  label={a.OriginalFileName || a.AttachmentName}
                  color={a.AttachmentID === activeAtt ? 'primary' : 'default'}
                  onClick={() => setActiveAtt(a.AttachmentID)} />
              ))}
            </Box>
          )}
          {fileUrl && isImage && (
            <Box style={{ flex: 1, overflow: 'auto', textAlign: 'center', background: '#f5f5f5' }}>
              <img src={fileUrl} alt="receipt" style={{ maxWidth: '100%', height: 'auto' }} />
            </Box>
          )}
          {fileUrl && !isImage && (
            <iframe title="receipt-file" src={fileUrl} style={{ flex: 1, width: '100%', border: 'none' }} />
          )}
        </Box>

        {/* right: details + line items + allocation */}
        <Box style={{ flex: 2, minWidth: 340, overflowY: 'auto', padding: 16 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="textSecondary">Details</Typography>
            <AspireLink href={aspireLink.receipt(receipt.ReceiptID)} label="Receipt in Aspire" />
          </Box>
          <Typography variant="body2">
            {receipt.VendorName || `Vendor ${receipt.VendorID || '—'}`}
            {receipt.VendorInvoiceNum ? ` · inv ${receipt.VendorInvoiceNum}` : ''}
            {` · ${receipt.ReceiptStatusName || ''}`}
            {` · ${fmtDate(receipt.ReceivedDate)}`}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            WT {receipt.WorkTicketNumber || '—'}
            <AspireLink href={receipt.WorkTicketID ? aspireLink.workTicket(receipt.WorkTicketID) : null} label="Work ticket" />
          </Typography>
          {receipt.ReceiptNote && <Typography variant="caption" color="textSecondary">{receipt.ReceiptNote}</Typography>}

          <Box mt={2}>
            <Typography variant="subtitle2" color="textSecondary">Line items ({(receipt.ReceiptItems || []).length})</Typography>
            <List dense>
              {(receipt.ReceiptItems || []).map((it, i) => (
                <ListItem key={it.ReceiptItemID || i} disableGutters>
                  <ListItemText
                    primary={`${it.ItemName || '—'} · ${money(it.ItemExtendedCost != null ? it.ItemExtendedCost : (it.ItemQuantity || 0) * (it.ItemUnitCost || 0))}`}
                    secondary={`${it.ItemQuantity || 0} × ${money(it.ItemUnitCost)}${it.ItemType ? ` · ${it.ItemType}` : ''}`}
                  />
                </ListItem>
              ))}
              {!(receipt.ReceiptItems || []).length && <Typography variant="body2" color="textSecondary">No line items.</Typography>}
            </List>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Attribute to invoice groups (%)</Typography>
            {!groups.length && <Typography variant="body2" color="textSecondary">Create invoice groups first.</Typography>}
            <Box style={{ border: '1px solid #e0e0e0', borderRadius: 6, overflow: 'hidden' }}>
              {groups.map((g, i) => {
                const has = pct[g._id] != null && pct[g._id] !== '' && Number(pct[g._id]) > 0
                return (
                  <Box
                    key={g._id}
                    display="flex" alignItems="center"
                    px={1.5} py={0.75}
                    style={{
                      gap: 12,
                      borderTop: i === 0 ? 'none' : '1px solid #eee',
                      background: has ? '#eef6ec' : 'transparent'
                    }}
                  >
                    <Typography variant="body2" style={{ flexGrow: 1, fontWeight: has ? 600 : 400 }}>{g.name}</Typography>
                    <TextField
                      size="small" type="number" variant="outlined"
                      value={pct[g._id] != null ? pct[g._id] : ''}
                      onChange={e => setPct(p => ({ ...p, [g._id]: e.target.value }))}
                      onBlur={persist}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      inputProps={{ min: 0, max: 100, style: { width: 48, textAlign: 'right', padding: '6px 4px' } }}
                    />
                  </Box>
                )
              })}
            </Box>
            {!!groups.length && (
              <Box mt={1} display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 6 }}>
                <Typography variant="body2" color="textSecondary">Total</Typography>
                <Typography variant="body2" style={{ color: ok ? '#33691e' : '#c62828', fontWeight: 700 }}>
                  {total}%{ok ? ' ✓' : ' ≠100'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      )}
    </Dialog>
  )
}

const ReceiptsPanel = ({ url, draftId, reloadKey, hasOpportunities, groups, savedAllocations, onSaveAllocations, locked, onReceiptsLoaded }) => {
  const [receipts, setReceipts] = useState(null)
  const [error, setError] = useState(null)
  const [showList, setShowList] = useState(false)
  const [reviewIdx, setReviewIdx] = useState(null)   // index of receipt open in review window
  useEffect(() => {
    if (!hasOpportunities) { setReceipts([]); onReceiptsLoaded && onReceiptsLoaded([]); return }
    let active = true
    setReceipts(null); setError(null)
    axios.get(`${url}/api/bid-estimation/invoice-grouping/drafts/${draftId}/receipts`)
      .then(res => { if (active) { const rr = res.data.receipts || []; setReceipts(rr); onReceiptsLoaded && onReceiptsLoaded(rr) } })
      .catch(e => { if (active) setError(e.response?.data?.error || e.message) })
    return () => { active = false }
  }, [url, draftId, reloadKey, hasOpportunities, onReceiptsLoaded])

  if (!hasOpportunities) return null
  const total = (receipts || []).reduce((s, r) => s + (r.ReceiptTotalCost || 0), 0)

  // saved allocations keyed by receiptId -> { groupId: percent } (for the review window)
  const savedByReceiptId = (savedAllocations || []).reduce((m, a) => {
    m[a.receiptId] = (a.splits || []).reduce((mm, s) => { mm[String(s.groupId)] = s.percent; return mm }, {})
    return m
  }, {})
  // Save ONE receipt's splits: merge into the full set and PATCH wholesale.
  const saveOne = (receipt, splits) => {
    const others = (savedAllocations || []).filter(a => a.receiptId !== receipt.ReceiptID)
    const next = splits.length
      ? [...others, { receiptId: receipt.ReceiptID, receiptNumber: receipt.ReceiptNumber, splits }]
      : others
    onSaveAllocations(next)
  }

  return (
    <Card style={{ marginBottom: 16 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Purchase Receipts{receipts ? ` (${receipts.length} · ${money(total)})` : ''}
          </Typography>
          {receipts && !!receipts.length && (
            <Button size="small" variant="outlined" onClick={() => setReviewIdx(0)}>Review receipts</Button>
          )}
        </Box>
        {error && <Typography variant="body2" color="error">{error}</Typography>}
        {receipts === null && <Box mt={1}><CircularProgress size={20} /></Box>}
        {receipts && !receipts.length && !error && (
          <Typography variant="body2" color="textSecondary">No receipts found for the linked opportunities.</Typography>
        )}
        {receipts && !!receipts.length && (
          <AllocationMatrix
            groups={groups}
            locked={locked}
            rowColLabel="Receipt"
            heading="Attribute each receipt to invoice groups (%)"
            emptyRowsText="No receipts to attribute."
            rows={receipts.map(r => ({
              key: String(r.ReceiptID),
              receiptId: r.ReceiptID, receiptNumber: r.ReceiptNumber, receipt: r,
              remaining: r.remainingPercent != null ? r.remainingPercent : 100,
              primary: `#${r.ReceiptNumber || r.ReceiptID} · ${r.VendorName || `Vendor ${r.VendorID || '—'}`} · ${money(r.ReceiptTotalCost)}`,
              secondary: [
                `${(r.ReceiptItems || []).length} item${(r.ReceiptItems || []).length === 1 ? '' : 's'}`,
                (r.invoicedPercent || 0) > 0 ? `${r.invoicedPercent}% invoiced` : null,
                r.VendorInvoiceNum && `inv ${r.VendorInvoiceNum}`,
                r.ReceiptStatusName, fmtDate(r.ReceivedDate)
              ].filter(Boolean).join(' · ')
            }))}
            savedByKey={(savedAllocations || []).reduce((m, a) => {
              m[String(a.receiptId)] = (a.splits || []).reduce((mm, s) => { mm[String(s.groupId)] = s.percent; return mm }, {})
              return m
            }, {})}
            buildAllocation={(r, splits) => ({ receiptId: r.receiptId, receiptNumber: r.receiptNumber, splits })}
            onSave={onSaveAllocations}
            rowAction={r => <Button size="small" onClick={() => setReviewIdx(receipts.findIndex(x => x.ReceiptID === r.receiptId))}>View</Button>}
          />
        )}
        {receipts && !!receipts.length && (
          <Box mt={2}>
            <Button size="small" onClick={() => setShowList(v => !v)}>
              {showList ? 'Hide' : 'Show'} {receipts.length} receipts
            </Button>
          </Box>
        )}
        {receipts && !!receipts.length && showList && (
          <List dense>
            {receipts.slice(0, 200).map(r => (
              <ListItem key={r.ReceiptID} disableGutters>
                <ListItemText
                  primary={`#${r.ReceiptNumber || r.ReceiptID} · ${r.VendorName || `Vendor ${r.VendorID || '—'}`} · ${money(r.ReceiptTotalCost)}${r.IsPurchaseCredit ? ' (credit)' : ''}`}
                  secondary={[
                    `${(r.ReceiptItems || []).length} item${(r.ReceiptItems || []).length === 1 ? '' : 's'}`,
                    r.VendorInvoiceNum && `inv ${r.VendorInvoiceNum}`,
                    `WT ${r.WorkTicketNumber || '—'}`, r.ReceiptStatusName, fmtDate(r.ReceivedDate)
                  ].filter(Boolean).join(' · ')}
                />
                <AspireLink href={aspireLink.receipt(r.ReceiptID)} label="Aspire" />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
      {reviewIdx != null && receipts && receipts[reviewIdx] && (
        <ReceiptReviewWindow
          url={url} draftId={draftId}
          receipts={receipts} startIndex={reviewIdx}
          groups={groups} savedByReceiptId={savedByReceiptId}
          onSaveOne={saveOne}
          onClose={() => setReviewIdx(null)}
        />
      )}
    </Card>
  )
}

/* ── grouping board (scoped to one draft) ──────────────────────── */
const Board = ({ url, draftId, onBack }) => {
  const [board, setBoard] = useState(null)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState({})       // lineItemId -> true
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [newGroupOpen, setNewGroupOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [editingGroupId, setEditingGroupId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [importing, setImporting] = useState(false)
  const [editingPriceId, setEditingPriceId] = useState(null)  // lineItemId being priced
  const [priceDraft, setPriceDraft] = useState('')
  const [receipts, setReceipts] = useState([])  // reported up from ReceiptsPanel (for material math)
  const [crews, setCrews] = useState([])        // reported up from VisitsPanel (for labor math)
  const [stages, setStages] = useState([])      // invoice stages within this draft
  const [activeStageId, setActiveStageId] = useState(null)
  const [draftNameEdit, setDraftNameEdit] = useState('')
  const [invoiceNumberEdit, setInvoiceNumberEdit] = useState('')
  const fileInputRef = useRef(null)

  const base = `${url}/api/bid-estimation/invoice-grouping`
  const load = useCallback(() => {
    axios.get(`${base}/drafts/${draftId}/board`)
      .then(res => { setBoard(res.data); setError(null) })
      .catch(e => setError(e.response?.data?.error || e.message))
  }, [base, draftId])
  const loadStages = useCallback(() => {
    return axios.get(`${base}/drafts/${draftId}/stages`)
      .then(res => {
        const st = res.data || []
        setStages(st)
        // keep active selection valid; default to first draft stage else first stage
        setActiveStageId(prev => {
          if (prev && st.some(s => s._id === prev)) return prev
          const firstDraft = st.find(s => s.status === 'draft')
          return (firstDraft || st[0])?._id || null
        })
        return st
      })
      .catch(() => setStages([]))
  }, [base, draftId])
  useEffect(() => { load(); loadStages() }, [load, loadStages])

  const activeStage = useMemo(
    () => stages.find(s => s._id === activeStageId) || null,
    [stages, activeStageId]
  )
  const stageLocked = activeStage?.status === 'approved'
  useEffect(() => {
    setDraftNameEdit(board?.draft?.name || '')
  }, [board?.draft?.name])
  useEffect(() => {
    setInvoiceNumberEdit(activeStage?.invoiceNumber || '')
  }, [activeStageId, activeStage?.invoiceNumber])

  const itemById = useMemo(() => {
    const m = {}
    for (const li of board?.lineItems || []) m[li._id] = li
    return m
  }, [board])
  // Line items already invoiced (locked: can't reprice or remove from a group).
  const invoicedItemSet = useMemo(
    () => new Set((board?.draft?.invoicedItemIds || []).map(String)),
    [board]
  )
  const isInvoiced = (id) => invoicedItemSet.has(String(id))
  const groupedIds = useMemo(() => {
    const s = new Set()
    for (const g of board?.groups || []) for (const id of g.lineItemIds) s.add(id)
    return s
  }, [board])
  const ungrouped = useMemo(
    () => (board?.lineItems || []).filter(li => !groupedIds.has(li._id)),
    [board, groupedIds]
  )
  const selectedIds = Object.keys(selected).filter(id => selected[id])
  const sumSell = (items) => items.reduce((s, li) => s + effectiveTotal(li), 0)
  const grandTotal = sumSell(board?.lineItems || [])
  const groupedTotal = sumSell((board?.lineItems || []).filter(li => groupedIds.has(li._id)))

  // Material/Labor per group reflect the ACTIVE invoice stage's attributions.
  const materialByGroupId = useMemo(() => {
    const totalById = new Map((receipts || []).map(r => [r.ReceiptID, Number(r.ReceiptTotalCost) || 0]))
    const out = {}
    for (const a of activeStage?.receiptAllocations || []) {
      const receiptTotal = totalById.get(a.receiptId)
      if (!receiptTotal) continue
      for (const s of a.splits || []) {
        const gid = String(s.groupId)
        out[gid] = (out[gid] || 0) + receiptTotal * (Number(s.percent) || 0) / 100
      }
    }
    return out
  }, [receipts, activeStage])

  const laborByGroupId = useMemo(() => {
    const costByRoute = new Map((crews || []).map(c => [c.routeId, Number(c.laborCost) || 0]))
    const out = {}
    for (const a of activeStage?.allocations || []) {
      const crewCost = costByRoute.get(a.routeId)
      if (!crewCost) continue
      for (const s of a.splits || []) {
        const gid = String(s.groupId)
        out[gid] = (out[gid] || 0) + crewCost * (Number(s.percent) || 0) / 100
      }
    }
    return out
  }, [crews, activeStage])

  // Customer-billed material is the receipt cost allocated to this stage,
  // plus the same flat 15% markup used by the packet. Calculate per receipt so
  // rounding matches the PDF exactly.
  const stageMaterialBilling = useMemo(() => {
    const costByReceipt = new Map((receipts || []).map(receipt => [
      Number(receipt.ReceiptID),
      Number(receipt.ReceiptTotalCost) || 0
    ]))
    let cost = 0
    let markup = 0
    for (const allocation of activeStage?.receiptAllocations || []) {
      const percent = Math.max(0, Math.min(100,
        (allocation.splits || []).reduce((sum, split) => sum + (Number(split.percent) || 0), 0)))
      const allocatedCost = Math.round((costByReceipt.get(Number(allocation.receiptId)) || 0) * percent) / 100
      cost += allocatedCost
      markup += Math.round(allocatedCost * 0.15 * 100) / 100
    }
    return { cost, markup, total: cost + markup }
  }, [receipts, activeStage])

  // Draft-wide rollup across grouped items only (what will actually be invoiced).
  const rollup = useMemo(() => {
    const allGroups = board?.groups || []
    const gids = allGroups.map(g => String(g._id))
    const material = gids.reduce((s, id) => s + (materialByGroupId[id] || 0), 0)
    const labor = gids.reduce((s, id) => s + (laborByGroupId[id] || 0), 0)
    const cost = material + labor
    // Invoice total = sell price of the groups marked Ready to Invoice.
    const readyGroups = allGroups.filter(g => g.readyToInvoice)
    const sellOfGroup = (g) => sumSell(g.lineItemIds.map(id => itemById[id]).filter(Boolean))
    const invoiceTotal = readyGroups.reduce((s, g) => s + sellOfGroup(g), 0)
    return { sell: groupedTotal, material, labor, cost, gap: groupedTotal - cost, invoiceTotal, readyCount: readyGroups.length }
  }, [board, materialByGroupId, laborByGroupId, groupedTotal, itemById])

  const refreshAfter = (promise) => promise.then(load).catch(e => {
    window.alert(e.response?.data?.error || e.message)
    load()
  })
  const addSelectedToGroup = (group) => {
    setMenuAnchor(null); setSelected({})
    refreshAfter(axios.patch(`${base}/groups/${group._id}`, {
      lineItemIds: [...group.lineItemIds, ...selectedIds]
    }))
  }
  const createGroup = () => {
    const name = newGroupName.trim()
    setNewGroupOpen(false); setNewGroupName(''); setSelected({})
    if (!name) return
    refreshAfter(axios.post(`${base}/drafts/${draftId}/groups`, { name, lineItemIds: selectedIds }))
  }
  const removeFromGroup = (group, lineItemId) =>
    refreshAfter(axios.patch(`${base}/groups/${group._id}`, {
      lineItemIds: group.lineItemIds.filter(id => id !== lineItemId)
    }))
  const renameGroup = (group) => {
    const name = editingName.trim()
    setEditingGroupId(null)
    if (name && name !== group.name) refreshAfter(axios.patch(`${base}/groups/${group._id}`, { name }))
  }
  const toggleReady = (group) =>
    refreshAfter(axios.patch(`${base}/groups/${group._id}`, { readyToInvoice: !group.readyToInvoice }))
  const saveOpportunities = (opps) =>
    refreshAfter(axios.patch(`${base}/drafts/${draftId}`, { opportunities: opps }))
  const saveDraftName = () => {
    const name = draftNameEdit.trim()
    if (!name) {
      setDraftNameEdit(board?.draft?.name || '')
      return
    }
    if (name === board?.draft?.name) return
    axios.patch(`${base}/drafts/${draftId}`, { name })
      .then(res => {
        setBoard(current => current
          ? { ...current, draft: { ...current.draft, ...res.data } }
          : current)
        return loadStages()
      })
      .catch(saveError => {
        window.alert(saveError.response?.data?.error || saveError.message)
        setDraftNameEdit(board?.draft?.name || '')
      })
  }
  // Stage-scoped saves: attributions live on the active invoice stage, and the
  // endpoint rejects (400) anything that would exceed an item's remaining %.
  const stageBase = `${base}/drafts/${draftId}/stages/${activeStageId}`
  const refreshStagesAfter = (promise) => promise
    .then(loadStages).then(() => { load() })
    .catch(e => { window.alert(e.response?.data?.error || e.message); loadStages() })
  const saveAllocations = (allocations) => {
    if (!activeStageId || stageLocked) return
    refreshStagesAfter(axios.patch(`${stageBase}/allocations`, { allocations }))
  }
  const saveReceiptAllocations = (allocations) => {
    if (!activeStageId || stageLocked) return
    refreshStagesAfter(axios.patch(`${stageBase}/receipt-allocations`, { allocations }))
  }
  const saveStageVisits = (visitIds) => {
    if (!activeStageId || stageLocked) return Promise.reject(new Error('Invoice stage is locked'))
    return axios.patch(`${stageBase}/visits`, { visitIds })
      .then(res => {
        setStages(current => current.map(stage => stage._id === res.data._id ? res.data : stage))
        return res.data
      })
      .catch(e => {
        window.alert(e.response?.data?.error || e.message)
        throw e
      })
  }
  const saveLaborHourAdjustments = (adjustments) => {
    if (!activeStageId || stageLocked) return Promise.reject(new Error('Invoice stage is locked'))
    return axios.patch(`${stageBase}/labor-hour-adjustments`, { adjustments })
      .then(res => {
        setStages(current => current.map(stage => stage._id === res.data._id ? res.data : stage))
        return res.data
      })
      .catch(e => {
        window.alert(e.response?.data?.error || e.message)
        throw e
      })
  }
  const saveInvoiceNumber = () => {
    if (!activeStageId) return
    const invoiceNumber = invoiceNumberEdit.trim()
    if (invoiceNumber === (activeStage?.invoiceNumber || '')) return
    axios.patch(`${stageBase}/metadata`, { invoiceNumber })
      .then(res => {
        setStages(current => current.map(stage => stage._id === res.data._id ? res.data : stage))
      })
      .catch(saveError => {
        window.alert(saveError.response?.data?.error || saveError.message)
        setInvoiceNumberEdit(activeStage?.invoiceNumber || '')
      })
  }
  // Progress billing: what % of each line's estimate THIS invoice bills. The
  // server caps cumulative % at 100 across approved stages, so a line can never
  // be billed past its estimate.
  const billPctByItem = useMemo(
    () => new Map((activeStage?.billedItems || []).map(b => [String(b.item), Number(b.percent) || 0])),
    [activeStage]
  )
  // What earlier APPROVED invoices already took, per line.
  const priorByItem = useMemo(() => {
    const out = new Map()
    const markupIds = new Set(
      (board?.lineItems || []).filter(isMaterialMarkupLine).map(item => String(item._id))
    )
    let frozenMaterialMarkup = 0
    for (const s of stages) {
      if (s.status !== 'approved' || s._id === activeStageId) continue
      const hasMaterialSnapshot = !!s.materialBilling?.calculatedAt
      if (hasMaterialSnapshot) frozenMaterialMarkup += Number(s.materialBilling.markup) || 0
      for (const b of s.billedItems || []) {
        const k = String(b.item)
        if (hasMaterialSnapshot && markupIds.has(k)) continue
        const cur = out.get(k) || { percent: 0, amount: 0 }
        cur.percent += Number(b.percent) || 0
        cur.amount += Number(b.amount) || 0
        out.set(k, cur)
      }
    }
    for (const markupId of markupIds) {
      const current = out.get(markupId) || { percent: 0, amount: 0 }
      current.amount += frozenMaterialMarkup
      out.set(markupId, current)
    }
    return out
  }, [stages, activeStageId, board])
  // Send the whole set every time — the endpoint replaces it wholesale, which
  // keeps "cleared to 0" from being indistinguishable from "not mentioned".
  const saveItemBilling = (nextByItem) => {
    if (!activeStageId || stageLocked) return
    const items = [...nextByItem.entries()]
      .filter(([, pct]) => pct > 0)
      .map(([itemId, percent]) => ({ itemId, percent }))
    refreshStagesAfter(axios.patch(`${stageBase}/item-billing`, { items }))
  }
  const setItemBillPct = (itemId, percent) => {
    const next = new Map(billPctByItem)
    next.set(String(itemId), Math.max(0, Math.min(100, Number(percent) || 0)))
    saveItemBilling(next)
  }
  // Seed every line in a group to one percent, then override individual lines.
  const setGroupBillPct = (group, percent) => {
    const pct = Math.max(0, Math.min(100, Number(percent) || 0))
    const next = new Map(billPctByItem)
    for (const id of group.lineItemIds || []) {
      if (isMaterialMarkupLine(itemById[id])) continue
      const remaining = 100 - ((priorByItem.get(String(id)) || {}).percent || 0)
      next.set(String(id), Math.min(pct, Math.max(0, remaining)))
    }
    saveItemBilling(next)
  }
  // The progress-billed schedule is the controlling invoice amount. Its line
  // values already include the project's material and labor value, so allocated
  // receipt cost is carved out to reveal labor rather than added a second time.
  const billing = useMemo(() => {
    let scheduleSubtotal = 0
    for (const g of (board?.groups || [])) {
      if (!g.readyToInvoice) continue
      for (const id of g.lineItemIds || []) {
        const li = itemById[id]
        if (!li || isMaterialMarkupLine(li)) continue
        scheduleSubtotal += effectiveTotal(li) * ((billPctByItem.get(String(id)) || 0) / 100)
      }
    }
    const hasMarkupLine = (board?.lineItems || []).some(isMaterialMarkupLine)
    const thisInvoice = scheduleSubtotal + (hasMarkupLine ? stageMaterialBilling.markup : 0)
    return {
      scheduleSubtotal,
      labor: thisInvoice - stageMaterialBilling.total,
      materialCost: stageMaterialBilling.cost,
      materialMarkup: stageMaterialBilling.markup,
      thisInvoice
    }
  }, [board, itemById, billPctByItem, stageMaterialBilling])

  const saveHoursOverride = (hoursOverride) =>
    refreshAfter(axios.patch(`${base}/drafts/${draftId}`, { hoursOverride }))
  const saveLaborPoolRates = (laborPoolRates) =>
    axios.patch(`${base}/drafts/${draftId}/labor-pool-rates`, { laborPoolRates })
      .then(res => {
        setBoard(current => current
          ? { ...current, draft: { ...current.draft, laborPoolRates: res.data?.laborPoolRates || [] } }
          : current)
        return res.data
      })
      .catch(error => {
        window.alert(error.response?.data?.error || error.message)
        throw error
      })
  const saveContactLaborPool = (contactId, laborPool) =>
    axios.patch(`${base}/drafts/${draftId}/contacts/${contactId}/labor-pool`, { laborPool })
      .then(res => {
        if (res.data?.packetsStaled) loadStages()
        return res.data?.person || res.data
      })
      .catch(e => {
        window.alert(e.response?.data?.error || e.message)
        throw e
      })
  // stage lifecycle
  const createStage = () =>
    refreshStagesAfter(axios.post(`${base}/drafts/${draftId}/stages`, {}).then(res => setActiveStageId(res.data._id)))
  const setStageStatus = (stageId, status) =>
    refreshStagesAfter(axios.patch(`${base}/drafts/${draftId}/stages/${stageId}/status`, { status }))
  const deleteStage = (stageId) => {
    if (!window.confirm('Delete this invoice stage? Its attributions are removed.')) return
    refreshStagesAfter(axios.delete(`${base}/drafts/${draftId}/stages/${stageId}`))
  }
  // Invoice packet: the generated customer PDF (billed items + receipt annex).
  // The stage payload already carries `packet`, so loadStages() is the refresh
  // mechanism and there is no separate packet state to keep in sync.
  const packet = activeStage?.packet || null
  const packetBuilding = ['queued', 'building'].includes(packet?.status)
  const buildPacket = () =>
    axios.post(`${stageBase}/packet`)
      .then(loadStages)
      .catch(e => window.alert(e.response?.data?.error || e.message))
  // Poll only while a build is actually in flight, then stop. loadStages is a
  // useCallback, so it is stable across renders and safe to depend on without
  // restarting the interval every tick.
  useEffect(() => {
    if (!activeStageId || !packetBuilding) return
    const t = setInterval(loadStages, 3000)
    return () => clearInterval(t)
  }, [activeStageId, packetBuilding, loadStages])
  const deleteGroup = (group) => {
    if (window.confirm(`Delete group "${group.name}"? Its items become ungrouped.`)) {
      refreshAfter(axios.delete(`${base}/groups/${group._id}`))
    }
  }
  const startEditPrice = (li) => {
    setEditingPriceId(li._id)
    setPriceDraft(li.importedTotal != null ? String(li.importedTotal) : '')
  }
  const savePrice = (li) => {
    const raw = priceDraft.trim()
    setEditingPriceId(null)
    const val = raw === '' ? null : Number(raw.replace(/[$,]/g, ''))
    if (raw !== '' && !Number.isFinite(val)) return
    if (val === (li.importedTotal ?? null)) return  // no change
    refreshAfter(axios.put(`${url}/api/bid-estimation/line-items/${li._id}`, { importedTotal: val }))
  }
  const importPrices = (file) => {
    if (!file) return
    setImporting(true)
    const form = new FormData()
    form.append('priceFile', file)
    axios.post(`${base}/projects/${board.project._id}/import-prices`, form)
      .then(res => {
        const { matched, parsed, unmatched = [] } = res.data || {}
        window.alert(
          `Imported prices for ${matched} of ${parsed} line item${parsed === 1 ? '' : 's'}.` +
          (unmatched.length ? `\nNo matching line item for item #: ${unmatched.join(', ')}.` : '')
        )
        load()
      })
      .catch(e => window.alert(e.response?.data?.error || e.message))
      .then(() => setImporting(false))
  }

  if (error) return <Box mt={6} textAlign="center"><Typography color="error">{error}</Typography><Button onClick={onBack}>Back to search</Button></Box>
  if (!board) return <Box mt={6} textAlign="center"><CircularProgress /></Box>

  return (
    <Box>
      {/* header */}
      <Box display="flex" alignItems="baseline" flexWrap="wrap" mb={2} style={{ gap: 16 }}>
        <Button size="small" onClick={onBack}>← Drafts</Button>
        <Box style={{ minWidth: 280 }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Draft name"
            value={draftNameEdit}
            onChange={event => setDraftNameEdit(event.target.value)}
            onBlur={saveDraftName}
            onKeyDown={event => { if (event.key === 'Enter') event.target.blur() }}
            inputProps={{ maxLength: 160, style: { fontSize: 18, fontWeight: 600 } }}
          />
          <Typography variant="caption" color="textSecondary">{board.project.name}</Typography>
        </Box>
        <Chip
          label={`Invoice Total ${fmt(billing.thisInvoice)} · ${rollup.readyCount} ready`}
          style={{ backgroundColor: '#1b5e20', color: '#fff', fontWeight: 600 }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`Labor ${fmt(billing.labor)} + materials ${fmt(billing.materialCost)} + 15% ${fmt(billing.materialMarkup)}`}
          style={billing.labor < 0 ? { color: '#c62828', borderColor: '#c62828' } : undefined}
        />
        <Chip size="small" label={`Grand total ${fmt(grandTotal)}`} />
        <Chip size="small" color="primary" label={`Grouped ${fmt(groupedTotal)}`} />
        <Chip size="small" variant="outlined" label={`Ungrouped ${fmt(grandTotal - groupedTotal)}`} />
        <Chip size="small" variant="outlined" label={`Cost ${fmt(rollup.cost)}`} style={{ color: '#4527a0' }} />
        <Chip size="small" label={`Gap ${fmt(rollup.gap)}${rollup.sell > 0 ? ` (${((rollup.gap / rollup.sell) * 100).toFixed(0)}%)` : ''}`}
          style={{ backgroundColor: rollup.gap < 0 ? '#c62828' : '#33691e', color: '#fff' }} />
        <Box flexGrow={1} />
        <Button
          size="small" variant="outlined" disabled={importing}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          {importing ? 'Importing…' : 'Import prices (CSV/Excel)'}
        </Button>
        <input
          ref={fileInputRef} type="file" hidden
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={e => { const f = e.target.files[0]; e.target.value = ''; importPrices(f) }}
        />
      </Box>

      {/* Aspire jobs this invoice covers */}
      <Box mb={2}>
        <OpportunityPicker url={url} value={board.draft?.opportunities} onChange={saveOpportunities} />
      </Box>

      {/* Staged invoices: switch, approve, add. Approved stages lock their %. */}
      <Card style={{ marginBottom: 16 }}>
        <CardContent style={{ paddingtop: 12, paddingBottom: 12 }}>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
            <Typography variant="subtitle2" style={{ marginRight: 4 }}>Invoices:</Typography>
            {stages.map(s => (
              <Chip
                key={s._id}
                label={`${invoiceNumberLabel(s.invoiceNumber, s.name)}${s.visitSelectionConfigured ? ` · ${(s.selectedVisitIds || []).length} visits` : ''}${s.status === 'approved' ? ' ✓' : ''}`}
                color={s._id === activeStageId ? 'primary' : 'default'}
                variant={s._id === activeStageId ? 'default' : 'outlined'}
                onClick={() => setActiveStageId(s._id)}
                style={s.status === 'approved' && s._id !== activeStageId ? { borderColor: '#4caf50', color: '#2e7d32' } : undefined}
              />
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={createStage}>New invoice</Button>
            <TextField
              size="small"
              variant="outlined"
              label="Invoice number"
              placeholder="e.g. 1042"
              value={invoiceNumberEdit}
              disabled={!activeStage}
              onChange={event => setInvoiceNumberEdit(event.target.value)}
              onBlur={saveInvoiceNumber}
              onKeyDown={event => { if (event.key === 'Enter') event.target.blur() }}
              inputProps={{ maxLength: 100 }}
              style={{ width: 170 }}
            />
            <Box flexGrow={1} />
            {activeStage && (stageLocked
              ? <>
                  <Chip size="small" label={`Approved ${activeStage.approvedByName ? `by ${activeStage.approvedByName}` : ''}`} style={{ backgroundColor: '#4caf50', color: '#fff' }} />
                  {packetBuilding && (
                    <Chip size="small" variant="outlined" label="Building packet…" />
                  )}
                  {packet?.status === 'ready' && (
                    <>
                      <Button
                        size="small"
                        href={`${url}/api/bid-estimation/invoice-grouping/drafts/${draftId}/stages/${activeStage._id}/packet/file`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {`Download packet${packet.pageCount ? ` (${packet.pageCount} pp.` : ''}${packet.sizeBytes ? `, ${(packet.sizeBytes / 1048576).toFixed(1)} MB)` : packet.pageCount ? ')' : ''}`}
                      </Button>
                      <Button size="small" onClick={buildPacket}>Rebuild packet</Button>
                    </>
                  )}
                  {packet?.status === 'stale' && (
                    <Chip size="small" variant="outlined" label="Packet out of date" onClick={buildPacket} />
                  )}
                  {packet?.status === 'failed' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      style={{ borderColor: '#c62828', color: '#c62828' }}
                      label="Packet failed — retry"
                      onClick={buildPacket}
                    />
                  )}
                  {(!packet || packet.status === 'none') && (
                    <Button size="small" onClick={buildPacket}>Generate packet</Button>
                  )}
                  <Button size="small" onClick={() => setStageStatus(activeStage._id, 'draft')}>Unapprove</Button>
                </>
              : <>
                  <Button size="small" variant="contained" color="primary" onClick={() => setStageStatus(activeStage._id, 'approved')}>Approve invoice</Button>
                  {stages.length > 1 && <Button size="small" onClick={() => deleteStage(activeStage._id)}>Delete</Button>}
                </>
            )}
          </Box>
          {stageLocked && (
            <Typography variant="caption" color="textSecondary">
              This invoice is approved and locked. Its attributed %’s are consumed — switch to or create a new invoice to bill the remaining amounts.
            </Typography>
          )}
        </CardContent>
      </Card>

      <VisitsPanel
        url={url} draftId={draftId}
        stageId={activeStageId}
        hasOpportunities={!!(board.draft?.opportunities || []).length}
        reloadKey={[
          (board.draft?.opportunities || []).map(o => o.OpportunityID).sort().join(','),
          `h:${board.draft?.hoursOverride ?? ''}`,
          `appr:${stages.filter(s => s.status === 'approved').map(s => s._id).join(',')}`,
          `stage:${activeStageId || ''}`
        ].join('|')}
        groups={board.groups || []}
        savedAllocations={activeStage?.allocations || []}
        onSaveAllocations={saveAllocations}
        onSaveVisits={saveStageVisits}
        onSavePersonLaborPool={saveContactLaborPool}
        onSaveLaborHourAdjustments={saveLaborHourAdjustments}
        locked={stageLocked}
        onCrewsLoaded={setCrews}
        hoursOverride={board.draft?.hoursOverride}
        onSaveHoursOverride={saveHoursOverride}
        laborPoolRates={board.draft?.laborPoolRates || []}
        onSaveLaborPoolRates={saveLaborPoolRates}
      />

      <ReceiptsPanel
        url={url} draftId={draftId}
        hasOpportunities={!!(board.draft?.opportunities || []).length}
        reloadKey={[
          (board.draft?.opportunities || []).map(o => o.OpportunityID).sort().join(','),
          `appr:${stages.filter(s => s.status === 'approved').map(s => s._id).join(',')}`
        ].join('|')}
        groups={board.groups || []}
        savedAllocations={activeStage?.receiptAllocations || []}
        onSaveAllocations={saveReceiptAllocations}
        locked={stageLocked}
        onReceiptsLoaded={setReceipts}
      />

      <Box display="flex" alignItems="flex-start" style={{ gap: 16 }}>
        {/* left: ungrouped items */}
        <Card style={{ flex: 5, minWidth: 0 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Ungrouped line items ({ungrouped.length})</Typography>
              <Box>
                <Button size="small" disabled={!selectedIds.length} onClick={e => setMenuAnchor(e.currentTarget)}>
                  Add {selectedIds.length ? `${selectedIds.length} ` : ''}to group…
                </Button>
              </Box>
            </Box>
            <List dense>
              {ungrouped.map(li => (
                <ListItem key={li._id} button onClick={() => setSelected(s => ({ ...s, [li._id]: !s[li._id] }))}>
                  <ListItemIcon><Checkbox edge="start" size="small" checked={!!selected[li._id]} tabIndex={-1} disableRipple /></ListItemIcon>
                  <ListItemText
                    primary={`${li.itemNo != null ? `#${li.itemNo} — ` : ''}${li.description}`}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box component="span" display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 4 }}>
                        <span>{`${li.quantity || 0} ${li.unit || ''} · cost ${fmt(li.totalCost)} · sell `}</span>
                        {editingPriceId === li._id ? (
                          <TextField
                            size="small" autoFocus value={priceDraft}
                            placeholder="0.00"
                            onClick={e => e.stopPropagation()}
                            onChange={e => setPriceDraft(e.target.value)}
                            onBlur={() => savePrice(li)}
                            onKeyDown={e => { if (e.key === 'Enter') savePrice(li); if (e.key === 'Escape') setEditingPriceId(null) }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            style={{ width: 130 }}
                          />
                        ) : isInvoiced(li._id) ? (
                          <span style={{ fontWeight: 500 }}>
                            {fmt(effectiveTotal(li))} 🔒
                          </span>
                        ) : (
                          <span
                            role="button"
                            onClick={e => { e.stopPropagation(); startEditPrice(li) }}
                            style={{ cursor: 'pointer', textDecoration: 'underline dotted', fontWeight: 500 }}
                          >
                            {effectiveTotal(li) > 0 ? `${fmt(effectiveTotal(li))}${isImported(li) ? ' (entered)' : ''}` : 'set price'}
                          </span>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {!ungrouped.length && <Typography variant="body2" color="textSecondary">Everything is grouped. 🎉</Typography>}
            </List>
          </CardContent>
        </Card>

        {/* right: groups */}
        <Box style={{ flex: 4, minWidth: 0 }}>
          <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={() => setNewGroupOpen(true)} style={{ marginBottom: 12 }}>
            New invoice group{selectedIds.length ? ` (with ${selectedIds.length} selected)` : ''}
          </Button>
          {(board.groups || []).map(g => {
            const items = g.lineItemIds.map(id => itemById[id]).filter(Boolean)
            const sell = sumSell(items)
            const attribCost = (materialByGroupId[String(g._id)] || 0) + (laborByGroupId[String(g._id)] || 0)
            const gap = sell - attribCost
            const marginPct = sell > 0 ? (gap / sell) * 100 : null
            return (
              <Card key={g._id} style={{ marginBottom: 12, border: g.readyToInvoice ? '1px solid #4caf50' : undefined }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    {editingGroupId === g._id ? (
                      <TextField
                        size="small" value={editingName} autoFocus
                        onChange={e => setEditingName(e.target.value)}
                        onBlur={() => renameGroup(g)}
                        onKeyDown={e => e.key === 'Enter' && renameGroup(g)}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography variant="h6" style={{ cursor: 'pointer' }}
                          onClick={() => { setEditingGroupId(g._id); setEditingName(g.name) }}>
                          {g.name}
                        </Typography>
                        {g.readyToInvoice && <Chip size="small" label="Ready to Invoice" style={{ backgroundColor: '#4caf50', color: '#fff' }} />}
                      </Box>
                    )}
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" style={{ marginRight: 8 }}>{fmt(sumSell(items))}</Typography>
                      <IconButton size="small" onClick={() => deleteGroup(g)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={<Switch size="small" color="primary" checked={!!g.readyToInvoice} onChange={() => toggleReady(g)} />}
                    label={<Typography variant="caption">Ready to Invoice</Typography>}
                    style={{ marginLeft: 0 }}
                  />
                  <Box display="flex" flexWrap="wrap" alignItems="baseline" style={{ gap: 12 }}>
                    <Typography variant="caption" style={{ color: '#1565c0', fontWeight: 600 }}>
                      Material {fmt(materialByGroupId[String(g._id)] || 0)}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#6a1b9a', fontWeight: 600 }}>
                      Labor {fmt(laborByGroupId[String(g._id)] || 0)}
                    </Typography>
                  </Box>
                  {/* Delta: sell − (material+labor). Gap = markup + still-missing costs
                      (drive time, QA/supervisor, equipment). Negative = cost exceeds sell. */}
                  <Box mt={0.5} py={0.5} px={1} style={{ background: gap < 0 ? '#fdecea' : '#f1f8e9', borderRadius: 4, display: 'inline-block' }}>
                    <Typography variant="caption" style={{ fontWeight: 600, color: gap < 0 ? '#c62828' : '#33691e' }}>
                      Gap {fmt(gap)}{marginPct != null ? ` (${marginPct.toFixed(0)}% margin)` : ''}
                      {' '}= sell {fmt(sell)} − cost {fmt(attribCost)}
                    </Typography>
                  </Box>
                  {/* Progress billing. Seeding the group fills every line; each
                      line can then be overridden — Mobilization often bills 100%
                      on invoice #1 while the rest of its group trickles. */}
                  <Box mt={1} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                    <Typography variant="caption" color="textSecondary">Bill this invoice:</Typography>
                    <TextField
                      size="small" type="number" disabled={stageLocked}
                      inputProps={{ min: 0, max: 100, step: 5, style: { width: 56, textAlign: 'right' } }}
                      InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
                      placeholder="0"
                      onKeyDown={e => { if (e.key === 'Enter') { setGroupBillPct(g, e.target.value); e.target.blur() } }}
                      onBlur={e => { if (e.target.value !== '') { setGroupBillPct(g, e.target.value); e.target.value = '' } }}
                    />
                    <Typography variant="caption" style={{ fontWeight: 600, color: '#1b5e20' }}>
                      = {fmt(items.reduce((s, li) => s + (
                        isMaterialMarkupLine(li)
                          ? billing.materialMarkup
                          : effectiveTotal(li) * ((billPctByItem.get(String(li._id)) || 0) / 100)
                      ), 0))}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">of {fmt(sell)} estimate</Typography>
                  </Box>
                  <List dense>
                    {items.map(li => {
                      const est = effectiveTotal(li)
                      const prior = priorByItem.get(String(li._id)) || { percent: 0, amount: 0 }
                      const pct = billPctByItem.get(String(li._id)) || 0
                      const remaining = Math.max(0, 100 - prior.percent)
                      const autoMarkup = isMaterialMarkupLine(li)
                      return (
                        <ListItem key={li._id} disableGutters>
                          <ListItemText
                            primary={`${li.itemNo != null ? `#${li.itemNo} — ` : ''}${li.description}`}
                            secondary={
                              `${fmt(est)} est${isImported(li) ? ' (entered)' : ''}` +
                              (autoMarkup ? ` · automatically bills 15% of this invoice's allocated materials` : '') +
                              (prior.amount > 0
                                ? (autoMarkup
                                    ? ` · ${fmt(prior.amount)} previously invoiced`
                                    : ` · ${fmt(prior.amount)} previously invoiced (${prior.percent.toFixed(0)}%)`)
                                : '') +
                              (remaining <= 0 ? ' · fully billed 🔒' : '')
                            }
                          />
                          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                            {/* Uncontrolled + commit on blur/Enter: each save
                                round-trips and reloads the board, so saving per
                                keystroke would fight the user's typing. `key`
                                re-seeds the field whenever the server value moves. */}
                            <TextField
                              key={`${li._id}:${pct}`}
                              size="small" type="number" defaultValue={pct === 0 ? '' : pct}
                              disabled={stageLocked || remaining <= 0 || autoMarkup}
                              placeholder={autoMarkup ? 'Auto' : '0'}
                              inputProps={{ min: 0, max: remaining, step: 5, style: { width: 48, textAlign: 'right' } }}
                              InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
                              onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                              onBlur={e => {
                                const next = Math.max(0, Math.min(remaining, Number(e.target.value) || 0))
                                if (next !== pct) setItemBillPct(li._id, next)
                              }}
                            />
                            <Typography variant="caption" style={{ minWidth: 74, textAlign: 'right', fontWeight: pct > 0 ? 600 : 400 }}>
                              {fmt(autoMarkup ? billing.materialMarkup : est * (pct / 100))}
                            </Typography>
                            {isInvoiced(li._id)
                              ? null
                              : <IconButton size="small" onClick={() => removeFromGroup(g, li._id)}><CloseIcon fontSize="small" /></IconButton>}
                          </Box>
                        </ListItem>
                      )
                    })}
                    {!items.length && <Typography variant="body2" color="textSecondary">Empty — add items from the left.</Typography>}
                  </List>
                </CardContent>
              </Card>
            )
          })}
          {!(board.groups || []).length && (
            <Typography variant="body2" color="textSecondary" align="center">
              No invoice groups yet. Select items on the left, then create one.
            </Typography>
          )}
        </Box>
      </Box>

      {/* add-to-group menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {(board.groups || []).map(g => <MenuItem key={g._id} onClick={() => addSelectedToGroup(g)}>{g.name}</MenuItem>)}
        <MenuItem onClick={() => { setMenuAnchor(null); setNewGroupOpen(true) }}><AddIcon fontSize="small" />&nbsp;New group…</MenuItem>
      </Menu>

      {/* new group dialog */}
      <Dialog open={newGroupOpen} onClose={() => setNewGroupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New invoice group</DialogTitle>
        <DialogContent>
          <TextField fullWidth autoFocus label="Group name" value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createGroup()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewGroupOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={!newGroupName.trim()} onClick={createGroup}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/* ── page shell: draft id lives in ?draftId= so refresh restores it ── */
const InvoiceGroupingPage = ({ url, history, location }) => {
  const draftId = new URLSearchParams(location.search).get('draftId')
  const setDraftId = (id) =>
    history.replace(id ? `${location.pathname}?draftId=${id}` : location.pathname)
  return (
    <Container maxWidth="xl" style={{ paddingTop: 24, paddingBottom: 48 }}>
      {draftId
        ? <Board url={url} draftId={draftId} onBack={() => setDraftId(null)} />
        : <DraftList url={url} onOpen={setDraftId} />}
    </Container>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(InvoiceGroupingPage)
